<template>
  <v-app>
    <HeaderDealerDashboard
      :userName="userName"
      :businessName="businessName"
      :navigation="navigation"
      @navigate="handleNavigation"
    />
    
    <v-main class="grey lighten-4">
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 font-weight-bold text-grey-800">Settings</h1>
            <p class="text-subtitle-1 text-grey-600 mt-2">Manage your dealer profile and preferences</p>
          </div>
        </div>

        <v-row>
          <v-col cols="12">
            <v-card elevation="3" class="pa-6">
              <!-- Account Management Section -->
              <div class="mb-8">
                <div class="d-flex justify-space-between align-center mb-4">
                  <h2 class="text-h5 font-weight-bold text-grey-800">Account Management</h2>
                </div>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-4 text-center">
                      <span class="material-icons-round mb-2" style="font-size: 48px; color: #1976d2;">person</span>
                      <h3 class="text-h6 mb-2">Profile Settings</h3>
                      <p class="text-body-2 text-grey-600 mb-3">Manage your personal information, contact details, and account preferences</p>
                      <v-btn color="primary" @click="$router.push('/dealers/profile')">
                        <span class="material-icons-round mr-1">edit</span>
                        Edit Profile
                      </v-btn>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-4 text-center">
                      <span class="material-icons-round mb-2" style="font-size: 48px; color: #f57c00;">lock</span>
                      <h3 class="text-h6 mb-2">Security Settings</h3>
                      <p class="text-body-2 text-grey-600 mb-3">Change your password and manage security preferences</p>
                      <v-btn color="orange" @click="$router.push('/dealers/profile')">
                        <span class="material-icons-round mr-1">security</span>
                        Manage Security
                      </v-btn>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="mb-8"></v-divider>

              <!-- Business Profile Section -->
              <div class="mb-8">
                <div class="d-flex justify-space-between align-center mb-4">
                  <h2 class="text-h5 font-weight-bold text-grey-800">Business Profile</h2>
                  <v-btn color="primary" text @click="editBusinessProfile">
                    <span class="material-icons-round mr-1">edit</span>
                    Edit
                  </v-btn>
                </div>
                
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="mb-3">
                      <span class="text-subtitle-2 text-grey-600">Dealership Name:</span>
                      <div class="text-body-1 font-weight-medium">{{ businessProfile.dealershipName }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="mb-3">
                      <span class="text-subtitle-2 text-grey-600">Contact:</span>
                      <div class="text-body-1 font-weight-medium">{{ businessProfile.contact }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <div class="mb-3">
                      <span class="text-subtitle-2 text-grey-600">Address:</span>
                      <div class="text-body-1 font-weight-medium">{{ businessProfile.address }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="mb-8"></v-divider>

              <!-- Payout Details Section -->
              <div class="mb-8">
                <div class="d-flex justify-space-between align-center mb-4">
                  <h2 class="text-h5 font-weight-bold text-grey-800">Payout Details</h2>
                  <v-btn color="primary" text @click="updatePayoutDetails">
                    <span class="material-icons-round mr-1">update</span>
                    Update
                  </v-btn>
                </div>
                
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="mb-3">
                      <span class="text-subtitle-2 text-grey-600">Bank:</span>
                      <div class="text-body-1 font-weight-medium">{{ payoutDetails.bank }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="mb-3">
                      <span class="text-subtitle-2 text-grey-600">Avg Payout Time:</span>
                      <div class="text-body-1 font-weight-medium">{{ payoutDetails.avgPayoutTime }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="mb-8"></v-divider>

              <!-- Integrations Section -->
              <div class="mb-8">
                <div class="d-flex justify-space-between align-center mb-4">
                  <h2 class="text-h5 font-weight-bold text-grey-800">Integrations</h2>
                  <v-btn color="primary" text @click="manageIntegrations">
                    <span class="material-icons-round mr-1">settings</span>
                    Manage
                  </v-btn>
                </div>
                
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="mb-3">
                      <span class="text-subtitle-2 text-grey-600">DMS:</span>
                      <div class="text-body-1 font-weight-medium d-flex align-center">
                        {{ integrations.dms }}
                        <v-chip small color="success" class="ml-2">Connected</v-chip>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="mb-3">
                      <span class="text-subtitle-2 text-grey-600">Web Scraper:</span>
                      <div class="text-body-1 font-weight-medium d-flex align-center">
                        {{ integrations.webScraper }}
                        <v-chip small color="success" class="ml-2">Active</v-chip>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <div class="mb-3">
                      <span class="text-subtitle-2 text-grey-600">Webhook:</span>
                      <div class="text-body-1 font-weight-medium">{{ integrations.webhook }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="mb-8"></v-divider>

              <!-- Notifications Section -->
              <div>
                <div class="d-flex justify-space-between align-center mb-4">
                  <h2 class="text-h5 font-weight-bold text-grey-800">Notifications</h2>
                  <v-btn color="green" dark @click="saveNotifications">
                    <span class="material-icons-round mr-1">save</span>
                    Save
                  </v-btn>
                </div>
                
                <v-row>
                  <v-col cols="12" md="4">
                    <v-checkbox
                      v-model="notifications.email"
                      label="Email"
                      color="green"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-checkbox
                      v-model="notifications.sms"
                      label="SMS"
                      color="green"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-checkbox
                      v-model="notifications.push"
                      label="Push"
                      color="green"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- Business Profile Edit Dialog -->
    <v-dialog v-model="showBusinessProfileDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 primary--text">
          <span class="material-icons-round mr-2">business</span>
          Edit Business Profile
        </v-card-title>
        <v-card-text>
          <v-form ref="businessForm" v-model="businessFormValid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editBusinessForm.dealershipName"
                  label="Dealership Name"
                  :rules="[v => !!v || 'Dealership name is required']"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editBusinessForm.contact"
                  label="Contact"
                  :rules="[v => !!v || 'Contact is required']"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editBusinessForm.address"
                  label="Address"
                  :rules="[v => !!v || 'Address is required']"
                  rows="3"
                  outlined
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeBusinessProfileDialog">Cancel</v-btn>
          <v-btn 
            color="green" 
            dark
            @click="saveBusinessProfile" 
            :loading="saveBusinessLoading"
            :disabled="!businessFormValid"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payout Details Dialog -->
    <v-dialog v-model="showPayoutDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 primary--text">
          <span class="material-icons-round mr-2">account_balance</span>
          Update Payout Details
        </v-card-title>
        <v-card-text>
          <v-form ref="payoutForm" v-model="payoutFormValid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editPayoutForm.bank"
                  label="Bank Information"
                  :rules="[v => !!v || 'Bank information is required']"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editPayoutForm.avgPayoutTime"
                  label="Average Payout Time"
                  :rules="[v => !!v || 'Payout time is required']"
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closePayoutDialog">Cancel</v-btn>
          <v-btn 
            color="green"
            dark 
            @click="savePayoutDetails" 
            :loading="savePayoutLoading"
            :disabled="!payoutFormValid"
          >
            Update Details
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '../Layout/HeaderDealerDashboard.vue'
import FooterDealer from '../Layout/FooterDealer.vue'

export default {
  name: 'DealerSettings',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      userName: '',
      businessName: '',
      navigation: [
        { text: 'Dashboard', icon: 'dashboard', route: '/dealers/dashboard' },
        { text: 'Commission', icon: 'trending_up', route: '/dealers/commission' },
        { text: 'Inventory', icon: 'inventory', route: '/dealers/inventory' },
        { text: 'Distribution', icon: 'share', route: '/dealers/distribution' },
        { text: 'Referring Agents', icon: 'people', route: '/dealers/agents' },
        { text: 'Reviews', icon: 'star', route: '/dealers/reviews' },
        { text: 'Payouts', icon: 'payment', route: '/dealers/payouts' },
        { text: 'Profile', icon: 'person', route: '/dealers/profile' },
        { text: 'Settings', icon: 'settings', route: '/dealers/settings', active: true }
      ],
      businessProfile: {
        dealershipName: 'ABC Motors',
        contact: 'John Doe | (602) 555-0199',
        address: '123 Main St, Phoenix, AZ'
      },
      payoutDetails: {
        bank: '**** 4321 | Stripe Connected',
        avgPayoutTime: '3.0 days'
      },
      integrations: {
        dms: 'DealerCenter',
        webScraper: 'Active',
        webhook: 'https://api.autosyncx.com/hooks/abc'
      },
      notifications: {
        email: true,
        sms: true,
        push: false
      },
      showBusinessProfileDialog: false,
      showPayoutDialog: false,
      businessFormValid: false,
      payoutFormValid: false,
      saveBusinessLoading: false,
      savePayoutLoading: false,
      editBusinessForm: {
        dealershipName: '',
        contact: '',
        address: ''
      },
      editPayoutForm: {
        bank: '',
        avgPayoutTime: ''
      }
    }
  },
  async mounted() {
    this.loadUserData()
    await this.loadSettings()
  },
  methods: {
    handleNavigation(item) {
      // Navigation is handled by the HeaderDealerDashboard component
    },
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Your Business'
    },
    async loadSettings() {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          this.$router.push('/dealers/login')
          return
        }

        const response = await axios.get('/api/dealer/settings', {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.data.flag === 1) {
          const data = response.data.data
          this.businessProfile = {
            dealershipName: data.business_profile.dealership_name,
            contact: data.business_profile.contact,
            address: data.business_profile.address
          }
          this.payoutDetails = {
            bank: data.payout_details.bank,
            avgPayoutTime: data.payout_details.avg_payout_time
          }
          this.integrations = {
            dms: data.integrations.dms,
            webScraper: data.integrations.web_scraper,
            webhook: data.integrations.webhook
          }
          this.notifications = {
            email: data.notifications.email,
            sms: data.notifications.sms,
            push: data.notifications.push
          }
        }
      } catch (error) {
        console.error('Error loading settings:', error)
        if (error.response?.status === 401) {
          this.$router.push('/dealers/login')
        }
      }
    },
    editBusinessProfile() {
      this.editBusinessForm = { ...this.businessProfile }
      this.showBusinessProfileDialog = true
    },
    closeBusinessProfileDialog() {
      this.showBusinessProfileDialog = false
      this.editBusinessForm = {
        dealershipName: '',
        contact: '',
        address: ''
      }
      this.$refs.businessForm?.resetValidation()
    },
    async saveBusinessProfile() {
      try {
        if (!this.$refs.businessForm.validate()) {
          return
        }

        this.saveBusinessLoading = true

        const token = localStorage.getItem('token')
        const response = await axios.put('/api/dealer/business-profile', {
          dealership_name: this.editBusinessForm.dealershipName,
          contact: this.editBusinessForm.contact,
          address: this.editBusinessForm.address
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.data.flag === 1) {
          this.businessProfile = { ...this.editBusinessForm }
          this.$toast.success('Business profile updated successfully!')
          this.closeBusinessProfileDialog()
        } else {
          this.$toast.error(response.data.message || 'Error updating business profile')
        }
      } catch (error) {
        console.error('Error saving business profile:', error)
        this.$toast.error('Error updating business profile')
      } finally {
        this.saveBusinessLoading = false
      }
    },
    updatePayoutDetails() {
      this.editPayoutForm = { ...this.payoutDetails }
      this.showPayoutDialog = true
    },
    closePayoutDialog() {
      this.showPayoutDialog = false
      this.editPayoutForm = {
        bank: '',
        avgPayoutTime: ''
      }
      this.$refs.payoutForm?.resetValidation()
    },
    async savePayoutDetails() {
      try {
        if (!this.$refs.payoutForm.validate()) {
          return
        }

        this.savePayoutLoading = true

        const token = localStorage.getItem('token')
        const response = await axios.put('/api/dealer/payout-details', {
          bank: this.editPayoutForm.bank,
          avg_payout_time: this.editPayoutForm.avgPayoutTime
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.data.flag === 1) {
          this.payoutDetails = { ...this.editPayoutForm }
          this.$toast.success('Payout details updated successfully!')
          this.closePayoutDialog()
        } else {
          this.$toast.error(response.data.message || 'Error updating payout details')
        }
      } catch (error) {
        console.error('Error saving payout details:', error)
        this.$toast.error('Error updating payout details')
      } finally {
        this.savePayoutLoading = false
      }
    },
    manageIntegrations() {
      this.$toast.info('Integration management coming soon!')
    },
    async saveNotifications() {
      try {
        const token = localStorage.getItem('token')
        const response = await axios.put('/api/dealer/notifications', {
          email: this.notifications.email,
          sms: this.notifications.sms,
          push: this.notifications.push
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Notification preferences saved!')
        } else {
          this.$toast.error(response.data.message || 'Error saving notification preferences')
        }
      } catch (error) {
        console.error('Error saving notifications:', error)
        this.$toast.error('Error saving notification preferences')
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.text-grey-800 {
  color: #424242 !important;
}

.text-grey-600 {
  color: #757575 !important;
}

.v-chip {
  font-size: 12px;
  height: 24px;
}
</style>
